import KeyCloak from 'keycloak-js';

// Auth mode: Keycloak is the default for release/deploy. Set REACT_APP_USE_FIXED_AUTH=true
// only for local dev with a static token (then set REACT_APP_FIXED_AUTH_TOKEN).
//   REACT_APP_USE_FIXED_AUTH=true   -> fixed token (DEV only; must be explicit)
//   REACT_APP_USE_FIXED_AUTH=false  -> Keycloak (PROD)
//   (unset)                         -> Keycloak (PROD) — do not change for production
//
// IMPORTANT:
// `public/env-config.js` is loaded via a <script> tag and `window._env_` might not be
// available at module-evaluation time in all environments. So we decide "fixed vs KC"
// dynamically based on the current `window._env_` value.
const isFixedAuthEnabled = () => window._env_?.REACT_APP_USE_FIXED_AUTH === 'true';

// ----------------------
// Fixed-token auth (DEV)
// ----------------------
const fixed_initKeyCloak = (onInitCallback, onFailCallback) => {
  if (typeof onInitCallback === 'function') {
    onInitCallback();
  }
  return Promise.resolve();
};

const fixed_doLogin = () => {};
const fixed_doLogout = () => {
  localStorage.clear();
};

const fixed_getToken = () => window._env_?.REACT_APP_FIXED_AUTH_TOKEN || null;
const fixed_updateToken = () => Promise.resolve();
const fixed_getUsername = () => 'dev-user';
const fixed_getFirstName = () => 'Dev';
const fixed_isLoggedIn = () => !!fixed_getToken();
const fixed_hasRole = () => true;

const fixed_authHeader = () => {
  const token = fixed_getToken();
  return token ? `Bearer ${token}` : null;
};

// ----------------------
// Real Keycloak auth (PROD / normal)
// ----------------------
const _kc = new KeyCloak({
  realm: window._env_.REACT_APP_KC_REALM,
  url: window._env_.REACT_APP_KC_URL,
  'redirect-uri': window._env_.REACT_APP_KC_REDIRECT_URI,
  'ssl-required': window._env_.REACT_APP_KC_SSL_REQUIRED,
  'public-client': window._env_.REACT_APP_KC_PUBLIC_CLIENT,
  clientId: window._env_.REACT_APP_KC_CLIENT_ID
});

const kc_initKeyCloak = (onInitCallback, onFailCallback) => {
  _kc
    .init({
      onLoad: 'check-sso',
      pkceMethod: 'S256'
    })
    .then(() => {
      if (typeof onInitCallback === 'function') {
        onInitCallback();
      }
    })
    .catch((e) => {
      console.log(e);
      if (typeof onFailCallback === 'function') {
        onFailCallback();
      }
    });
};

const kc_doLogin = (locale) => {
  if (locale) {
    _kc.login({
      redirectUri: window._env_.REACT_APP_KC_REDIRECT_URI,
      locale: locale
    });
  } else {
    _kc.login({
      redirectUri: window._env_.REACT_APP_KC_REDIRECT_URI
    });
  }
};

const kc_doLogout = () => {
  localStorage.clear();
  _kc.logout({
    redirectUri: window._env_.REACT_APP_KC_REDIRECT_URI
  });
};

const kc_getToken = () => _kc.token;
const kc_updateToken = () => _kc.updateToken(5).catch(kc_doLogin);
const kc_getUsername = () => _kc.tokenParsed?.preferred_username;
const kc_getFirstName = () => _kc.tokenParsed?.given_name;
const kc_isLoggedIn = () => !!_kc.token;
const kc_hasRole = (role) => _kc.tokenParsed.realm_access.roles.includes(role);

const kc_authHeader = () => {
  const token = kc_getToken();
  return token ? `Bearer ${token}` : null;
};

// ----------------------
// Export dynamic wrapper
// ----------------------
// We route each call to the correct auth mode at runtime.
const AuthService = {
  initKeyCloak: (onInitCallback, onFailCallback) =>
    isFixedAuthEnabled()
      ? fixed_initKeyCloak(onInitCallback, onFailCallback)
      : kc_initKeyCloak(onInitCallback, onFailCallback),
  isLoggedIn: () => (isFixedAuthEnabled() ? fixed_isLoggedIn() : kc_isLoggedIn()),
  authHeader: () => (isFixedAuthEnabled() ? fixed_authHeader() : kc_authHeader()),
  doLogin: (locale) => (isFixedAuthEnabled() ? fixed_doLogin(locale) : kc_doLogin(locale)),
  doLogout: () => (isFixedAuthEnabled() ? fixed_doLogout() : kc_doLogout()),
  getToken: () => (isFixedAuthEnabled() ? fixed_getToken() : kc_getToken()),
  updateToken: () => (isFixedAuthEnabled() ? fixed_updateToken() : kc_updateToken()),
  getUsername: () => (isFixedAuthEnabled() ? fixed_getUsername() : kc_getUsername()),
  getFirstName: () =>
    isFixedAuthEnabled() ? fixed_getFirstName() : kc_getFirstName(),
  hasRole: (role) => (isFixedAuthEnabled() ? fixed_hasRole(role) : kc_hasRole(role))
};

export default AuthService;
